import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../../styles/theme';
import { useAccount } from '../WalletProvider';
import ReactMarkdown from 'react-markdown';

const LiveContextsSection = styled.section``;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 16px;
  background: ${theme.colors.washi};
  padding: 16px;
  border-radius: 24px;
  box-shadow: 
    0 4px 24px rgba(64, 145, 220, 0.05),
    0 1px 2px rgba(64, 145, 220, 0.03);
`;

const HeaderTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 12px;
  border-bottom: 1px solid rgba(64, 145, 220, 0.15);
`;

const Title = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${theme.colors.sumi};
  font-family: 'M PLUS Rounded 1c', sans-serif;
  display: flex;
  align-items: center;
  gap: 8px;

  &::before {
    content: '🔍';
    font-size: 16px;
  }
`;

const ViewControls = styled.div`
  display: flex;
  gap: 8px;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  background: ${({ $active }) => $active ? '#4091DC' : 'transparent'};
  color: ${({ $active }) => $active ? theme.colors.washi : theme.colors.sumi};
  border: 1px solid ${({ $active }) => $active ? 'transparent' : '#4091DC'};
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${({ $active }) => $active ? '#4091DC' : 'rgba(64, 145, 220, 0.1)'};
    transform: translateY(-1px);
  }
`;

const ContextGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 12px;
`;

const ContextCard = styled.div`
  background: ${theme.colors.washi};
  border: 1px solid rgba(64, 145, 220, 0.1);
  border-radius: 12px;
  padding: 16px;
  transition: all 0.2s;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 
      0 4px 24px rgba(64, 145, 220, 0.05),
      0 1px 2px rgba(64, 145, 220, 0.03);
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const AgentTag = styled.span`
  background: rgba(64, 145, 220, 0.1);
  color: #4091DC;
  padding: 3px 10px;
  border-radius: 8px;
  font-size: 11px;
  font-weight: 600;
  border: 1px solid rgba(64, 145, 220, 0.2);
`;

const ContextId = styled.span`
  color: ${theme.colors.sumi};
  font-family: 'Space Mono', monospace;
  font-size: 11px;
  opacity: 0.5;
`;

const MemoryPreview = styled.div`
  color: ${theme.colors.sumi};
  font-size: 14px;
  line-height: 1.6;
  margin-bottom: 16px;
  min-height: 60px;
  max-height: calc(1.6em * 4);
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const SearchInput = styled.input`
  width: 100%;
  max-width: 300px;
  padding: 8px 12px;
  border: 1px solid rgba(64, 145, 220, 0.2);
  border-radius: 6px;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 12px;
  color: ${theme.colors.sumi};
  background: rgba(255, 255, 255, 0.8);
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #4091DC;
    background: white;
    box-shadow: 0 2px 8px rgba(64, 145, 220, 0.1);
  }
`;

const FilterBar = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
`;

const ClearButton = styled.button`
  background: none;
  border: none;
  color: #4091DC;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 14px;
  cursor: pointer;
  padding: 4px 8px;
  border-radius: 4px;
  transition: all 0.2s;

  &:hover {
    background: rgba(64, 145, 220, 0.1);
  }
`;

const Description = styled.p`
  color: ${theme.colors.sumi};
  font-size: 12px;
  line-height: 1.5;
  margin: 8px 0;
  opacity: 0.8;
  min-height: 36px;
`;

const CardFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(64, 145, 220, 0.1);
`;

const Timestamp = styled.span`
  color: ${theme.colors.sumi};
  font-size: 10px;
  opacity: 0.5;
  font-family: 'Space Mono', monospace;
`;

const ViewContextButton = styled.button`
  background: transparent;
  color: #4091DC;
  border: 1px solid #4091DC;
  padding: 4px 10px;
  border-radius: 6px;
  font-family: 'M PLUS Rounded 1c', sans-serif;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: rgba(64, 145, 220, 0.1);
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const MemoryModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.washi};
  padding: 32px;
  border-radius: 16px;
  max-width: 800px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
  box-shadow: 0 4px 24px rgba(64, 145, 220, 0.1);

  .markdown {
    font-family: 'M PLUS Rounded 1c', sans-serif;
    line-height: 1.6;
    color: ${theme.colors.sumi};

    h1, h2, h3, h4, h5, h6 {
      margin-top: 24px;
      margin-bottom: 16px;
      font-weight: 600;
      color: #4091DC;
    }

    p {
      margin-bottom: 16px;
    }

    ul, ol {
      margin-bottom: 16px;
      padding-left: 24px;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  font-size: 24px;
  color: ${theme.colors.sumi};
  cursor: pointer;
  opacity: 0.6;
  transition: all 0.2s;

  &:hover {
    opacity: 1;
  }
`;

interface Context {
  id: string;
  name: string;
  description: string;
  timestamp: string;
}

interface ContextDetails {
  id: string;
  value: {
    memory: string;
    [key: string]: any;
  };
}

export function LiveContexts() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [contexts, setContexts] = useState<Context[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filterText, setFilterText] = useState('');
  const [selectedContext, setSelectedContext] = useState<ContextDetails | null>(null);
  const { account } = useAccount();

  const filteredContexts = contexts.filter(context => 
    context.id.toLowerCase().includes(filterText.toLowerCase()) ||
    context.name.toLowerCase().includes(filterText.toLowerCase()) ||
    context.description.toLowerCase().includes(filterText.toLowerCase())
  );

  const handleClearFilter = () => {
    setFilterText('');
  };

  const handleViewContext = async (contextId: string) => {
    if (!account?.api_key) {
      alert('Please connect your wallet to view context details');
      return;
    }

    try {
      const response = await fetch(`/api/pulse/${contextId}`, {
        headers: {
          'Authorization': `Bearer ${account.api_key}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch context details');
      }

      const data = await response.json();
      setSelectedContext(data);
    } catch (error) {
      console.error('Error viewing context:', error);
      alert('Failed to load context details');
    }
  };

  const handleCloseModal = () => {
    setSelectedContext(null);
  };

  useEffect(() => {
    const fetchContexts = async () => {
      try {
        const response = await fetch('/api/pulse/list');
        const data = await response.json();
        const contextIds = data.context_ids || [];
        
        const metaResponse = await fetch('/api/pulse/meta', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ context_ids: contextIds }),
        });
        
        const metaData = await metaResponse.json();
        const contextList = Object.entries(metaData).map(([id, data]: [string, any]) => ({
          id,
          name: data?.value?.name || 'Unnamed Context',
          description: data?.value?.description || 'No description available',
          timestamp: data?.value?.timestamp || new Date().toISOString()
        }));
        
        setContexts(contextList);
        setError(null);
      } catch (err) {
        setError('Failed to load contexts');
        console.error('Error fetching contexts:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchContexts();
    const interval = setInterval(fetchContexts, 30000);
    return () => clearInterval(interval);
  }, []);

  if (loading) {
    return (
      <LiveContextsSection>
        <SectionHeader>
          <HeaderTop>
            <Title>Agent Contexts</Title>
          </HeaderTop>
        </SectionHeader>
        <ContextGrid>
          {[1, 2, 3].map((i) => (
            <ContextCard key={i}>
              <CardHeader>
                <AgentTag>Loading...</AgentTag>
              </CardHeader>
              <MemoryPreview>Loading...</MemoryPreview>
            </ContextCard>
          ))}
        </ContextGrid>
      </LiveContextsSection>
    );
  }

  if (error) {
    return (
      <LiveContextsSection>
        <SectionHeader>
          <HeaderTop>
            <Title>Agent Contexts</Title>
          </HeaderTop>
        </SectionHeader>
        <ContextCard>
          <CardHeader>
            <AgentTag>Error</AgentTag>
          </CardHeader>
          <MemoryPreview>{error}</MemoryPreview>
        </ContextCard>
      </LiveContextsSection>
    );
  }

  return (
    <LiveContextsSection>
      <SectionHeader>
        <HeaderTop>
          <Title>Agent Contexts</Title>
          <ViewControls>
            <ViewButton
              $active={viewMode === 'grid'}
              onClick={() => setViewMode('grid')}
            >
              Grid View
            </ViewButton>
            <ViewButton
              $active={viewMode === 'list'}
              onClick={() => setViewMode('list')}
            >
              List View
            </ViewButton>
          </ViewControls>
        </HeaderTop>
        <FilterBar>
          <SearchInput
            placeholder="Filter by name, ID, or description..."
            value={filterText}
            onChange={(e) => setFilterText(e.target.value)}
          />
          {filterText && (
            <ClearButton onClick={handleClearFilter}>
              Clear
            </ClearButton>
          )}
        </FilterBar>
      </SectionHeader>
      <ContextGrid>
        {filteredContexts.map((context) => (
          <ContextCard key={context.id}>
            <CardHeader>
              <AgentTag>{context.name}</AgentTag>
              <ContextId>{context.id}</ContextId>
            </CardHeader>
            <Description>{context.description}</Description>
            <CardFooter>
              <Timestamp>{new Date(context.timestamp).toLocaleString()}</Timestamp>
              <ViewContextButton onClick={() => handleViewContext(context.id)}>
                View Context
              </ViewContextButton>
            </CardFooter>
          </ContextCard>
        ))}
      </ContextGrid>

      {selectedContext && (
        <MemoryModal onClick={handleCloseModal}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <CloseButton onClick={handleCloseModal}>&times;</CloseButton>
            <div className="markdown">
              <ReactMarkdown>{selectedContext.value.memory}</ReactMarkdown>
            </div>
          </ModalContent>
        </MemoryModal>
      )}
    </LiveContextsSection>
  );
} 